import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Map, { Source, Layer, NavigationControl, MapRef } from 'react-map-gl/mapbox';
import { FeatureCollection, Point } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import axiosInstance from '../../../helpers/api';
import AIRollingText from './AIRollingText';
import './MapShowcase.css';

interface Showcase {
  title: string;
  subtitle: string;
  filter: any;
  center: { lat: number; lng: number };
  zoom: number;
  city: string;
  aiDescription: string;
}

interface PropertyMini {
  id: string;
  mlsNumber: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  images?: string[];
  latitude?: number;
  longitude?: number;
  coordinates?: {
    lat: number;
    lng: number;
  };
  daysOnMarket?: number;
}

// Neighborhood insights data
const neighborhoodData: Record<string, any> = {
  'Mesa': {
    highlights: ['3rd Largest City in Arizona', 'Great Schools', 'Family-Friendly', 'Affordable Housing'],
    vibe: 'Mesa offers the perfect blend of suburban comfort and urban amenities. Known for its excellent schools, diverse neighborhoods, and growing tech scene.',
    stats: {
      population: '504K+',
      avgHome: '$425K',
      schools: '9/10 Rating',
      growth: '+12% (5yr)'
    },
    features: ['Mesa Arts Center', 'Cubs Spring Training', 'Light Rail Access', 'Historic Downtown'],
    bestFor: 'Families, First-Time Buyers, Tech Workers'
  },
  'Scottsdale': {
    highlights: ['Luxury Living', 'Golf Capital', 'Upscale Shopping', 'Resort Lifestyle'],
    vibe: 'Scottsdale defines luxury desert living with world-class golf courses, high-end shopping, and a vibrant nightlife scene in Old Town.',
    stats: {
      population: '242K+',
      avgHome: '$875K',
      schools: '9/10 Rating',
      growth: '+8% (5yr)'
    },
    features: ['Fashion Square Mall', 'TPC Scottsdale', 'Old Town Nightlife', '200+ Golf Courses'],
    bestFor: 'Luxury Buyers, Golf Enthusiasts, Professionals'
  },
  'Goodyear': {
    highlights: ['Fastest Growing City', 'New Communities', 'Family-Oriented', 'Spring Training Home'],
    vibe: 'Goodyear is the West Valley\'s rising star with master-planned communities, excellent amenities, and a strong sense of community.',
    stats: {
      population: '95K+',
      avgHome: '$485K',
      schools: '8/10 Rating',
      growth: '+45% (5yr)'
    },
    features: ['Estrella Mountain Park', 'Goodyear Ballpark', 'New Shopping Centers', 'Growing Job Market'],
    bestFor: 'Growing Families, Remote Workers, Baseball Fans'
  },
  'Paradise Valley': {
    highlights: ['Ultra-Luxury Estates', 'Mountain Views', 'Privacy & Prestige', 'Celebrity Haven'],
    vibe: 'Paradise Valley is Arizona\'s most exclusive address, offering sprawling estates with breathtaking views of Camelback Mountain.',
    stats: {
      population: '14K+',
      avgHome: '$3.2M',
      schools: '10/10 Rating',
      growth: '+5% (5yr)'
    },
    features: ['Camelback Mountain', 'Luxury Resorts', 'Elite Golf Clubs', 'Gated Communities'],
    bestFor: 'Luxury Estate Buyers, Celebrities, CEOs'
  },
  'Gilbert': {
    highlights: ['#1 Safest City', 'Top Schools', 'Young Families', 'Heritage District'],
    vibe: 'Gilbert transformed from a farming town to one of America\'s best places to live, perfect for families seeking safety and community.',
    stats: {
      population: '267K+',
      avgHome: '$525K',
      schools: '9/10 Rating',
      growth: '+18% (5yr)'
    },
    features: ['Heritage District', 'Riparian Preserve', 'SanTan Village', 'Family Parks'],
    bestFor: 'Young Families, Safety-Conscious Buyers, Professionals'
  },
  'Chandler': {
    highlights: ['Tech Hub', 'Price Corridor', 'Diverse Community', 'Great Schools'],
    vibe: 'Chandler balances high-tech employment with family-friendly neighborhoods, making it ideal for professionals and families alike.',
    stats: {
      population: '275K+',
      avgHome: '$475K',
      schools: '8/10 Rating',
      growth: '+15% (5yr)'
    },
    features: ['Chandler Fashion Center', 'Tech Companies', 'Downtown Chandler', 'Desert Breeze Park'],
    bestFor: 'Tech Workers, Families, Young Professionals'
  },
  'Tempe': {
    highlights: ['College Town', 'Urban Living', 'Light Rail', 'Diverse Culture'],
    vibe: 'Home to ASU, Tempe offers vibrant urban living with a youthful energy, cultural diversity, and excellent transit options.',
    stats: {
      population: '195K+',
      avgHome: '$415K',
      schools: '7/10 Rating',
      growth: '+10% (5yr)'
    },
    features: ['ASU Campus', 'Mill Avenue', 'Tempe Town Lake', 'Light Rail System'],
    bestFor: 'Young Professionals, Students, Urban Lifestyle Seekers'
  }
};

// Component to display neighborhood insights
const NeighborhoodInsights: React.FC<{city: string, currentShowcase: number}> = ({ city }) => {
  const data = neighborhoodData[city] || neighborhoodData['Mesa'];
  
  return (
    <div className="neighborhood-details">
      <div className="neighborhood-vibe mb-2">
        <p className="lead">{data.vibe}</p>
      </div>
      
      <div className="neighborhood-highlights mb-2">
        <h6 className="text-primary mb-1">Why {city}?</h6>
        <div className="highlight-tags">
          {data.highlights.map((highlight: string, index: number) => (
            <span key={index} className="badge bg-light text-dark me-2 mb-2">
              {highlight}
            </span>
          ))}
        </div>
      </div>
      
      <div className="neighborhood-stats mb-2">
        <h6 className="text-primary mb-1">Quick Facts</h6>
        <Row className="g-2">
          {Object.entries(data.stats).map(([key, value]) => (
            <Col xs={6} key={key}>
              <div className="stat-card">
                <div className="stat-value">{value as string}</div>
                <div className="stat-label">{key === 'avgHome' ? 'Avg Home' : key === 'schools' ? 'Schools' : key === 'population' ? 'Population' : 'Growth'}</div>
              </div>
            </Col>
          ))}
        </Row>
      </div>
      
      <div className="best-for">
        <h6 className="text-primary mb-2">Best For</h6>
        <p className="mb-0">{data.bestFor}</p>
      </div>
    </div>
  );
};

const MapShowcaseSimple: React.FC = () => {
  const navigate = useNavigate();
  const mapRef = useRef<MapRef>(null);
  const [currentShowcase, setCurrentShowcase] = useState(0);
  const [properties, setProperties] = useState<PropertyMini[]>([]);
  const [stats, setStats] = useState({ count: 0, median: '$0', newThisWeek: 0 });
  const [mapboxToken, setMapboxToken] = useState<string>('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [chatKey, setChatKey] = useState(0); // Force chat to restart typing

  const showcases: Showcase[] = [
    {
      title: "🌟 AI Found Your Gilbert Dream Home",
      subtitle: "Top Schools · Pool Homes · Family Neighborhoods",
      filter: { city: 'Gilbert' },
      center: { lat: 33.3528, lng: -111.7890 },
      zoom: 11,
      city: 'Gilbert',
      aiDescription: "Gilbert offers A+ schools, resort-style pools, and family-friendly neighborhoods with homes starting at $450K"
    },
    {
      title: "🎓 Perfect Tempe Homes Near ASU",
      subtitle: "Walk to Campus · Modern Condos · Investment Properties",
      filter: { city: 'Tempe' },
      center: { lat: 33.4255, lng: -111.9400 },
      zoom: 11,
      city: 'Tempe',
      aiDescription: "Walk to ASU campus from these Tempe properties - perfect for students or investors seeking 8%+ returns"
    },
    {
      title: "⛳ Luxury Scottsdale Golf Estates",
      subtitle: "Golf Course Views · Resort Living · $700K+",
      filter: { city: 'Scottsdale', minPrice: 700000 },
      center: { lat: 33.4942, lng: -111.9261 },
      zoom: 11,
      city: 'Scottsdale',
      aiDescription: "Luxury golf estates at TPC Scottsdale and Grayhawk with fairway views, wine rooms, and resort amenities"
    },
    {
      title: "🏙️ Downtown Phoenix High-Rise Living",
      subtitle: "City Views · Walk to Everything · Rooftop Pools",
      filter: { city: 'Phoenix' },
      center: { lat: 33.4484, lng: -112.0740 },
      zoom: 12,
      city: 'Phoenix',
      aiDescription: "Downtown Phoenix high-rises with rooftop pools, city views, and walk to Chase Field - starting at $350K"
    },
    {
      title: "🏊 Mesa Pool Homes Under $500K",
      subtitle: "Great Schools · Big Yards · Family Friendly",
      filter: { city: 'Mesa', maxPrice: 500000 },
      center: { lat: 33.4152, lng: -111.8315 },
      zoom: 11,
      city: 'Mesa',
      aiDescription: "Mesa family homes with pools and 3-car garages under $500K - Eastmark seeing 12% annual appreciation"
    },
    {
      title: "💎 Chandler Tech Corridor Homes",
      subtitle: "Near Intel & PayPal · New Construction · Smart Homes",
      filter: { city: 'Chandler' },
      center: { lat: 33.3062, lng: -111.8413 },
      zoom: 11,
      city: 'Chandler',
      aiDescription: "Chandler tech corridor homes near Intel & PayPal campuses - new builds with solar and smart home features"
    }
  ];

  // Fetch Mapbox token
  useEffect(() => {
    const fetchMapboxToken = async () => {
      try {
        const response = await axiosInstance.get('/properties/config/mapbox');
        if (response.data.mapboxToken) {
          setMapboxToken(response.data.mapboxToken);
          console.log('✅ Mapbox token loaded');
        }
      } catch (error) {
        console.error('❌ Failed to fetch Mapbox token:', error);
        // Use fallback token
        setMapboxToken('pk.eyJ1IjoiaG91c2VleiIsImEiOiJjbHQyZ2dyMm0wMGxxMmxuczR5dmlpOWxtIn0.s-vyWzYxKcc2c7ObD5gvqQ');
      }
    };

    fetchMapboxToken();
  }, []);

  // Auto-cycle through showcases
  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        const next = (currentShowcase + 1) % showcases.length;
        setCurrentShowcase(next);
        setChatKey(prev => prev + 1); // Force chat to restart
        setIsTransitioning(false);
      }, 300);
    }, 10000); // 10 seconds per showcase for better viewing
    return () => clearInterval(interval);
  }, [currentShowcase, showcases.length]);

  // Load properties when showcase changes
  useEffect(() => {
    if (mapboxToken) {
      loadShowcaseProperties(currentShowcase);
    }
  }, [currentShowcase, mapboxToken]);

  // Preload disabled to prevent server overload
  // // Preload next showcase properties
  // useEffect(() => {
  //   if (mapboxToken && properties.length > 0) {
  //     const nextIndex = (currentShowcase + 1) % showcases.length;
  //     // Preload next city's properties in background
  //     setTimeout(() => {
  //       const params = new URLSearchParams({
  //         city: showcases[nextIndex].city,
  //         limit: '100',
  //         ...showcases[nextIndex].filter
  //       });
  //       axiosInstance.get(`/mls-portal/properties/map?${params}`);
  //     }, 5000); // Preload after 5 seconds
  //   }
  // }, [currentShowcase, mapboxToken, properties.length]);

  // Fly to new location when showcase changes
  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      const showcase = showcases[currentShowcase];
      mapRef.current.flyTo({
        center: [showcase.center.lng, showcase.center.lat],
        zoom: showcase.zoom,
        duration: 2000
      });
    }
  }, [currentShowcase, mapLoaded]);

  const loadShowcaseProperties = async (showcaseIndex: number) => {
    try {
      const showcase = showcases[showcaseIndex];
      const params = new URLSearchParams({
        city: showcase.city,
        limit: '50', // Reduced to prevent server overload
        ...showcase.filter
      });

      const response = await axiosInstance.get(`/mls-portal/properties/map?${params}`);
      
      if (response.data.success && response.data.data) {
        // Debug coordinate format
        console.log(`🏠 Loaded ${response.data.data.length} properties for ${showcase.city}`);
        console.log('Sample property coordinates:', response.data.data[0]?.coordinates, response.data.data[0]?.latitude, response.data.data[0]?.longitude);
        
        // Use all properties for the map display
        setProperties(response.data.data);
        
        // Calculate stats from all properties
        const allProperties = response.data.data;
        const prices = allProperties.map((p: any) => p.price).filter((p: number) => p > 0);
        const median = prices.length > 0 ? prices.sort((a: number, b: number) => a - b)[Math.floor(prices.length / 2)] : 0;
        const newListings = allProperties.filter((p: any) => p.daysOnMarket && p.daysOnMarket < 7).length;
        
        setStats({
          count: allProperties.length,
          median: median > 0 ? `$${(median / 1000).toFixed(0)}K` : 'N/A',
          newThisWeek: newListings
        });
      }
    } catch (error) {
      console.error('Error loading properties:', error);
    }
  };

  const exploreLocation = (showcase: Showcase) => {
    // Create natural language query based on showcase - Theta AI will understand these
    let query = '';
    
    if (showcase.city === 'Gilbert') {
      query = 'pool homes with good schools in Gilbert';
    } else if (showcase.city === 'Tempe') {
      query = 'homes near ASU campus in Tempe';
    } else if (showcase.city === 'Scottsdale') {
      query = 'luxury golf course homes in Scottsdale over 700k';
    } else if (showcase.city === 'Phoenix') {
      query = 'downtown Phoenix condos under 600k';
    } else if (showcase.city === 'Mesa') {
      query = 'Mesa pool homes under 500k with good schools';
    } else if (showcase.city === 'Chandler') {
      query = 'family homes in Chandler tech corridor';
    } else {
      query = `homes in ${showcase.city}`;
    }
    
    console.log('🔍 Navigating to search with Theta AI query:', query);
    
    // Store the query in localStorage as fallback
    localStorage.setItem('chatRealEstateQuery', query);
    
    // Navigate to search - let Theta AI handle the natural language query
    navigate('/search', { 
      state: { 
        initialQuery: query,
        autoSearch: true
      } 
    });
  };

  const exploreProperty = (property: PropertyMini) => {
    // Construct a search query from the property address
    const searchQuery = `${property.address.street}, ${property.address.city}, ${property.address.state} ${property.address.zip}`;
    
    // Navigate to search with the property selected
    navigate('/search', {
      state: {
        initialQuery: searchQuery,
        selectedPropertyId: property.mlsNumber || property.id,
        autoSearch: true // Trigger automatic search on load
      }
    });
  };

  const jumpToShowcase = (index: number) => {
    setCurrentShowcase(index);
    setChatKey(prev => prev + 1); // Force chat to restart
  };

  // Convert properties to GeoJSON for clustering
  const propertyGeoJSON: FeatureCollection<Point> = useMemo(() => {
    const validProperties = properties.filter(property => {
      // Filter out properties without valid coordinates
      const lng = property.coordinates?.lng || property.longitude;
      const lat = property.coordinates?.lat || property.latitude;
      return lng && lat && lng !== 0 && lat !== 0;
    });
    
    console.log(`📍 Converting ${validProperties.length} properties to GeoJSON (from ${properties.length} total)`);
    
    return {
      type: 'FeatureCollection',
      features: validProperties.map(property => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            property.coordinates?.lng || property.longitude || 0,
            property.coordinates?.lat || property.latitude || 0
          ]
        },
        properties: {
          ...property,
          daysOnMarket: property.daysOnMarket || Math.floor(Math.random() * 30) // Mock data if not available
        }
      }))
    };
  }, [properties]);

  // Cluster layer configuration
  const clusterLayer: any = {
    id: 'clusters',
    type: 'circle',
    source: 'properties',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        '#10b981', 10,
        '#3b82f6', 50,
        '#8b5cf6', 100,
        '#dc2626'
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        18, 10,
        22, 50,
        26, 100,
        30
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff'
    }
  };

  const clusterCountLayer: any = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'properties',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12
    },
    paint: {
      'text-color': '#ffffff'
    }
  };

  const unclusteredPointLayer: any = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'properties',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'case',
        ['<', ['get', 'daysOnMarket'], 3], '#ef4444',     // Hot! Just listed - red
        ['<', ['get', 'daysOnMarket'], 7], '#10b981',     // New listing - green
        ['<', ['get', 'price'], 400000], '#3b82f6',       // Great deal - blue
        ['>', ['get', 'price'], 1000000], '#fbbf24',      // Luxury - gold
        '#8b5cf6'                                          // Default - purple
      ],
      'circle-radius': 12,
      'circle-stroke-width': 3,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.9
    }
  };

  if (!mapboxToken) {
    return (
      <div className="map-showcase-section">
        <Container>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <section className="map-showcase-section">
      <Container>
        <div className="showcase-header text-center mb-4">
          <h2>
            <span className="ai-badge pulse-animation">AI</span>
            {isTransitioning ? 'Searching...' : 'Discovered'} Your Perfect Neighborhood
          </h2>
          <p className="text-muted">
            Watch our AI explore {showcases.length} hot Valley locations in real-time
          </p>
        </div>

        <div className="showcase-stage">
          <Row>
            <Col lg={8}>
              <div className="map-canvas-wrapper">
                <Map
                  ref={mapRef}
                  initialViewState={{
                    longitude: showcases[0].center.lng,
                    latitude: showcases[0].center.lat,
                    zoom: showcases[0].zoom
                  }}
                  style={{ width: '100%', height: '700px' }}
                  mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                  mapboxAccessToken={mapboxToken}
                  onLoad={() => {
                    console.log('🗺️ Map loaded');
                    setMapLoaded(true);
                  }}
                  interactiveLayerIds={[]}
                  interactive={false}
                  scrollZoom={false}
                  doubleClickZoom={false}
                  dragPan={false}
                  dragRotate={false}
                  keyboard={false}
                >
                  <NavigationControl position="top-right" />
                  
                  {/* Property Data Source with Clustering */}
                  <Source
                    id="properties"
                    type="geojson"
                    data={propertyGeoJSON}
                    cluster={true}
                    clusterMaxZoom={13}
                    clusterRadius={40}
                    clusterProperties={{
                      hot: ['+', ['case', ['<', ['get', 'daysOnMarket'], 3], 1, 0]],
                      new: ['+', ['case', ['<', ['get', 'daysOnMarket'], 7], 1, 0]]
                    }}
                  >
                    <Layer {...clusterLayer} />
                    <Layer {...clusterCountLayer} />
                    <Layer {...unclusteredPointLayer} />
                  </Source>
                </Map>
                
                {/* Map Legend */}
                <div className="map-legend">
                  <div className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#ef4444' }}></span>
                    <span className="legend-label">Hot! New</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#10b981' }}></span>
                    <span className="legend-label">This Week</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#3b82f6' }}></span>
                    <span className="legend-label">Under $400K</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-dot" style={{ backgroundColor: '#fbbf24' }}></span>
                    <span className="legend-label">Luxury $1M+</span>
                  </div>
                </div>
                
                {/* Location Spotlight Overlay */}
                <div className="location-spotlight">
                  <div className="location-card">
                    <h3>{showcases[currentShowcase].title}</h3>
                    <p className="mb-3">{showcases[currentShowcase].subtitle}</p>
                    
                    <div className="location-stats">
                      <div className="stat">
                        <span className="number">{stats.count}</span>
                        <span className="label">Active Listings</span>
                      </div>
                      <div className="stat">
                        <span className="number">{stats.median}</span>
                        <span className="label">Median Price</span>
                      </div>
                      <div className="stat">
                        <span className="number">{stats.newThisWeek}</span>
                        <span className="label">New This Week</span>
                      </div>
                    </div>
                    
                    <Button 
                      variant="primary"
                      size="sm"
                      className="explore-btn pulse-animation"
                      onClick={() => exploreLocation(showcases[currentShowcase])}
                    >
                      <strong>🔍 Search {showcases[currentShowcase].city} Now</strong>
                    </Button>
                  </div>
                </div>

                {/* Progress Indicator */}
                <div className="showcase-progress">
                  {showcases.map((_, index) => (
                    <div 
                      key={index}
                      className={`progress-dot ${index === currentShowcase ? 'active' : ''}`}
                      onClick={() => jumpToShowcase(index)}
                    />
                  ))}
                </div>

                {/* AI Rolling Text */}
                <AIRollingText 
                  key={chatKey}
                  message={showcases[currentShowcase].aiDescription}
                  agentName="AI"
                />
              </div>
            </Col>

            <Col lg={4}>
              {/* Neighborhood Insights */}
              <div className="neighborhood-insights">
                <h5 className="mb-3">About {showcases[currentShowcase].city}</h5>
                <div className="insights-content">
                  <NeighborhoodInsights 
                    city={showcases[currentShowcase].city}
                    currentShowcase={currentShowcase}
                  />
                </div>

                {/* Location Quick Links */}
                <div className="location-navigator mt-4">
                  <p className="mb-2 text-muted small">Jump to location:</p>
                  <div className="location-pills">
                    {showcases.map((showcase, index) => (
                      <button
                        key={index}
                        className={`location-pill ${index === currentShowcase ? 'active' : ''}`}
                        onClick={() => jumpToShowcase(index)}
                      >
                        {showcase.city}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* MLS Disclaimer */}
                <div className="mls-disclaimer mt-3">
                  <small className="text-muted d-block">
                    Listing data courtesy of Arizona Regional Multiple Listing Service. 
                    Information deemed reliable but not guaranteed.
                  </small>
                  <small className="text-muted d-block mt-1">
                    Real estate listings held by brokerage firms other than KairosIQ AI Realty 
                    are marked with the ARMLS logo.
                  </small>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Container>
    </section>
  );
};

export default MapShowcaseSimple;